import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, DollarSign, Calendar, Star } from 'lucide-react';
import { theme } from '../../../theme';
import { useOwnerAnalytics, ChannelDataPoint } from '../../../hooks/venue/useOwnerAnalytics';
import { CHANNEL_LABELS, CHANNEL_COLORS, BookingChannel } from '../../../types/venue';
import { useOwnerDashboard } from '../../../hooks/venue/useOwnerDashboard';
import RevenueLineChart from '../../../components/venue/owner/RevenueLineChart';
import OccupancyHeatmap from '../../../components/venue/owner/OccupancyHeatmap';
import StatCard from '../../../components/venue/owner/StatCard';

// Get the Monday of the week containing the given date
function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatWeekLabel(monday: Date): string {
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const startDay = monday.getDate();
  const endDay = sunday.getDate();
  const startMonth = months[monday.getMonth()];
  const endMonth = months[sunday.getMonth()];
  const year = sunday.getFullYear();

  if (monday.getMonth() === sunday.getMonth()) {
    return `${startDay} - ${endDay} ${endMonth} ${year}`;
  }
  return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
}

const OwnerAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [weekMonday, setWeekMonday] = useState<Date>(() => getMonday(new Date()));
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');

  const weekLabel = formatWeekLabel(weekMonday);

  const { venues, fetchVenues } = useOwnerDashboard();
  const {
    summary,
    revenueData,
    occupancyData,
    channelData,
    loading,
    error,
    fetchSummary,
    fetchRevenueData,
    fetchOccupancyData,
    fetchChannelData,
    exportCSV,
  } = useOwnerAnalytics();

  // Fetch venues on mount
  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  // Set first venue as default
  useEffect(() => {
    if (venues.length > 0 && !selectedVenueId) {
      setSelectedVenueId(venues[0].id);
    }
  }, [venues, selectedVenueId]);

  // Fetch summary, revenue, and channel data when timeRange changes
  useEffect(() => {
    fetchSummary(timeRange);
    fetchRevenueData(timeRange, 'day');
    fetchChannelData(timeRange);
  }, [timeRange, fetchSummary, fetchRevenueData, fetchChannelData]);

  // Fetch occupancy when venue or week changes
  useEffect(() => {
    if (selectedVenueId) {
      fetchOccupancyData(selectedVenueId, weekLabel);
    }
  }, [selectedVenueId, weekLabel, fetchOccupancyData]);

  const handleExportCSV = () => {
    exportCSV('csv', timeRange);
  };

  // Transform revenue data to match RevenueLineChart expected format
  const transformedRevenueData = revenueData.map((item, index) => ({
    day: index + 1,
    revenue: item.revenue,
  }));

  const handlePreviousWeek = () => {
    setWeekMonday(prev => {
      const newMonday = new Date(prev);
      newMonday.setDate(newMonday.getDate() - 7);
      return newMonday;
    });
  };

  const handleNextWeek = () => {
    setWeekMonday(prev => {
      const newMonday = new Date(prev);
      newMonday.setDate(newMonday.getDate() + 7);
      return newMonday;
    });
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    minHeight: '100vh',
    paddingBottom: '48px',
  };

  const headerStyle: React.CSSProperties = {
    padding: '32px 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const breadcrumbRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const breadcrumbsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  };

  const breadcrumbStyle = (isActive: boolean): React.CSSProperties => ({
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    color: isActive ? theme.colors.text : theme.colors.textSecondary,
    cursor: isActive ? 'default' : 'pointer',
    textDecoration: 'none',
  });

  const separatorStyle: React.CSSProperties = {
    color: '#6C757D',
    fontSize: '14px',
  };

  const exportBtnStyle: React.CSSProperties = {
    padding: '14px 32px',
    borderRadius: '12px',
    backgroundColor: 'transparent',
    border: `2px solid ${theme.colors.primary}`,
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    fontWeight: '600',
    color: theme.colors.primary,
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  const titleRowStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  };

  const mainTitleStyle: React.CSSProperties = {
    fontFamily: theme.fonts.heading,
    fontSize: '48px',
    color: theme.colors.text,
    margin: 0,
    letterSpacing: '2px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    color: theme.colors.textSecondary,
    margin: 0,
  };

  const statsRowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    padding: '0 48px',
  };

  const chartsContainerStyle: React.CSSProperties = {
    padding: '24px 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
  };

  if (loading && !summary) {
    return (
      <div style={{ ...containerStyle, padding: '48px', textAlign: 'center' }}>
        Cargando analytics...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        ...containerStyle,
        padding: '48px',
        textAlign: 'center',
        color: theme.colors.danger
      }}>
        {error}
      </div>
    );
  }

  if (!summary) {
    return (
      <div style={{ ...containerStyle, padding: '48px', textAlign: 'center' }}>
        No hay datos de analytics disponibles
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={breadcrumbRowStyle}>
          <div style={breadcrumbsStyle}>
            <span style={breadcrumbStyle(false)} onClick={() => navigate('/owner/dashboard')}>
              Dashboard
            </span>
            <span style={separatorStyle}>/</span>
            <span style={breadcrumbStyle(true)}>Analytics</span>
          </div>
          <button style={exportBtnStyle} onClick={handleExportCSV}>
            Exportar CSV
          </button>
        </div>
        <div style={titleRowStyle}>
          <h1 style={mainTitleStyle}>ANALYTICS</h1>
          <p style={subtitleStyle}>Estadísticas de rendimiento de tus canchas</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={statsRowStyle}>
        <StatCard
          title="Revenue Total"
          value={`$${summary.totalRevenue.toLocaleString()}`}
          icon={DollarSign}
          iconColor={theme.colors.success}
          trend={{ value: summary.revenueChange, label: `+${summary.revenueChange}%` }}
        />
        <StatCard
          title="Reservas"
          value={summary.totalBookings.toLocaleString()}
          icon={Calendar}
          iconColor={theme.colors.primary}
          trend={{ value: summary.bookingsChange, label: `+${summary.bookingsChange}%` }}
        />
        <StatCard
          title="Ocupación Promedio"
          value={`${summary.averageOccupancy}%`}
          icon={TrendingUp}
          iconColor={theme.colors.primary}
          trend={{ value: summary.occupancyChange, label: `+${summary.occupancyChange}%` }}
        />
        <StatCard
          title="Rating Promedio"
          value={Number(summary.averageRating).toFixed(1)}
          subtitle="⭐⭐⭐⭐⭐"
          icon={Star}
          iconColor={theme.colors.warning}
        />
      </div>

      {/* Charts */}
      <div style={chartsContainerStyle}>
        <RevenueLineChart
          data={transformedRevenueData}
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          onExport={handleExportCSV}
        />
        <OccupancyHeatmap
          data={occupancyData}
          weekLabel={weekLabel}
          onPreviousWeek={handlePreviousWeek}
          onNextWeek={handleNextWeek}
        />

        {/* Channel Distribution */}
        {channelData.length > 0 && (
          <div style={{
            backgroundColor: theme.colors.surface,
            borderRadius: '18px',
            border: `1px solid ${theme.colors.border}`,
            padding: '24px',
          }}>
            <h3 style={{
              fontFamily: theme.fonts.heading,
              fontSize: '22px',
              color: theme.colors.text,
              margin: '0 0 20px 0',
              letterSpacing: '1px',
            }}>
              RESERVAS POR CANAL
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {channelData.map((item: ChannelDataPoint) => {
                const totalCount = channelData.reduce((sum: number, d: ChannelDataPoint) => sum + d.count, 0);
                const percentage = totalCount > 0 ? (item.count / totalCount) * 100 : 0;
                const channelKey = item.channel as BookingChannel;
                const color = CHANNEL_COLORS[channelKey] || '#757575';
                const label = CHANNEL_LABELS[channelKey] || item.channel;

                return (
                  <div key={item.channel} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                      width: '80px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: theme.colors.text,
                      flexShrink: 0,
                    }}>
                      {label}
                    </span>
                    <div style={{
                      flex: 1,
                      height: '24px',
                      backgroundColor: theme.colors.background,
                      borderRadius: '6px',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${percentage}%`,
                        height: '100%',
                        backgroundColor: color,
                        borderRadius: '6px',
                        minWidth: percentage > 0 ? '4px' : '0',
                        transition: 'width 0.3s',
                      }} />
                    </div>
                    <span style={{
                      width: '40px',
                      fontSize: '13px',
                      fontWeight: 700,
                      color: theme.colors.text,
                      textAlign: 'right',
                      flexShrink: 0,
                    }}>
                      {item.count}
                    </span>
                    <span style={{
                      width: '90px',
                      fontSize: '12px',
                      color: theme.colors.textSecondary,
                      textAlign: 'right',
                      flexShrink: 0,
                    }}>
                      ${item.revenue.toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerAnalyticsPage;
